<template>
  <div class="card-content">
    <v-card-actions class="ma-0 pa-0">
      <div class="timePeriodControl" ref="timePeriodControl">
        <span class="timePeriodControl__label">
          {{ $t('map.historyDialog.timePeriodLabel') }}
        </span>
        <div class="timePeriodControl__controls">
          <v-btn
            :color="getButtonColor('day')"
            variant="flat"
            size="small"
            @click="changeTimePeriod('day')"
            data-cy="historyTimePeriodDay"
            :disabled="isLoading"
            v-if="allowDay"
            >{{ $t('map.historyDialog.timePeriod.day') }}</v-btn
          >
          <v-tooltip location="bottom" :disabled="!onlyPublicDevices">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :color="getButtonColor('week')"
                variant="flat"
                size="small"
                @click="changeTimePeriod('week')"
                data-cy="historyTimePeriodWeek"
                :disabled="isLoading"
                v-if="allowWeek"
                :class="[{ restrictedContent: onlyPublicDevices }]"
                >{{ $t('map.historyDialog.timePeriod.week') }}</v-btn
              >
            </template>
            <span>{{ $t('map.historyDialog.publicDeviceTimespanMessage') }}</span>
          </v-tooltip>
          <v-tooltip location="bottom" :disabled="!onlyPublicDevices">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :color="getButtonColor('month')"
                variant="flat"
                size="small"
                @click="changeTimePeriod('month')"
                data-cy="historyTimePeriodMonth"
                :disabled="isLoading"
                v-if="allowMonth"
                :class="[{ restrictedContent: onlyPublicDevices }]"
                >{{ $t('map.historyDialog.timePeriod.month') }}</v-btn
              >
            </template>
            <span>{{ $t('map.historyDialog.publicDeviceTimespanMessage') }}</span>
          </v-tooltip>
          <v-tooltip location="bottom" :disabled="!onlyPublicDevices">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                :color="getButtonColor('threeMonths')"
                variant="flat"
                size="small"
                @click="changeTimePeriod('threeMonths')"
                data-cy="historyTimePeriodThreeMonths"
                :disabled="isLoading"
                v-if="allowThreeMonths"
                :class="[{ restrictedContent: onlyPublicDevices }]"
                >{{ $t('map.historyDialog.timePeriod.threeMonths') }}</v-btn
              >
            </template>
            <span>{{ $t('map.historyDialog.publicDeviceTimespanMessage') }}</span>
          </v-tooltip>
          <v-btn
            :color="getButtonColor('custom')"
            variant="flat"
            size="small"
            data-cy="historyTimePeriodCustom"
            :disabled="isLoading"
            v-if="showCustomButton"
            @click="$emit('selectCustomDateRange')"
          >
            <v-icon size="1.5rem">mdi-calendar</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card-actions>
    <div class="measurement-label-wrapper">
      <v-card-subtitle v-if="!fullscreen || xs" ref="historyGraphLabel" data-cy="historyGraphLabel"
        >{{ graphLabel }}
        <v-btn
          ref="customResetZoomButton"
          class="reset-zoom-button"
          variant="tonal"
          size="small"
          @click="customResetZoomButtonClickHandler"
        >
          {{ $t('map.historyDialog.resetZoom') }}
        </v-btn>
      </v-card-subtitle>
    </div>
    <div class="history-chart-wrapper highcharts-light">
      <div
        :style="`height: ${graphHeight}; width: ${graphWidth};`"
        class="graph"
        :class="{
          'opacity-20': isLoading,
          'empty-history-chart': !showData
        }"
        ref="graph"
        data-cy="historyGraph"
      ></div>
      <div v-if="isLoading" class="loading-container">
        <span v-if="showSpinnerWithPercentage">{{
          $t('map.historyDialog.graph.retrievingDataMessage')
        }}</span>
        <v-progress-circular
          :size="100"
          :width="5"
          :model-value="completedPercentage"
          indeterminate
          color="primary"
        >
          <span v-if="showSpinnerWithPercentage">{{ completedPercentage }}%</span>
        </v-progress-circular>
        <span v-if="showSpinnerWithPercentage">{{
          $t('map.historyDialog.graph.pleaseWaitMessage')
        }}</span>
      </div>
      <div v-if="!showData && !isLoading" class="empty-message" data-cy="historyGraphEmptyMessage">
        {{ $t('map.historyDialog.graph.emptyMessage') }}
      </div>
    </div>
    <v-row
      v-if="selectedDevices.length > 0"
      class="selected-devices"
      :class="{ 'opacity-20': isLoading }"
      ref="selectedDevices"
    >
      <div
        v-for="(device, index) in selectedDevices"
        :key="device.id"
        :class="{ inactive: !isDeviceActive(device) }"
        :style="`border-left: 4px solid ${graphColors[index]};`"
        :data-cy="`historyDialogDeviceName${index}`"
      >
        <v-tooltip location="bottom" max-width="600" :disabled="disableDeviceTooltip(device)">
          <template v-slot:activator="{ props }">
            <v-chip v-bind="props" variant="text" label size="small">
              <img
                v-if="isDeviceIndoor(device.id)"
                class="selected-device-icon"
                :src="indoorIconSrc(device.model)"
                alt="Indoor Device Icon"
              />
              <img
                v-else
                class="selected-device-icon"
                :src="outdoorIconSrc(device.model)"
                alt="Outdoor Device Icon"
              />
              <span v-if="device.friendlyName" class="device-name" :ref="device.id">
                <span>{{ device.friendlyName }}</span>
              </span>
              <span v-else class="device-name">
                {{ $t('map.popover.publicDevice') }} {{ index + 1 }}
              </span>
              <v-icon v-if="removableDevices" @click="removeDevice(index)" end> mdi-close </v-icon>
            </v-chip>
          </template>
          <span>{{ getDeviceTooltip(device) }}</span>
        </v-tooltip>
      </div>
    </v-row>
    <div v-if="selectedDevices.length === 5" class="maximum-met-message" ref="maximumMetMessage">
      {{ $t('map.historyDialog.maximumMet') }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw, nextTick, type PropType } from 'vue'
// ESM builds only: the UMD dist reads a `window._Highcharts` global that bundled builds never define.
import Highcharts from 'highcharts/esm/highstock'
import 'highcharts/esm/modules/boost-canvas'
import 'highcharts/esm/modules/boost'
import debounce from 'lodash/debounce'
import { FusionStores } from '@fusion/saas-components-v3'
import { getAuthInstance } from '@fusion/auth-v3'

import {
  getDefaultGraphOptions,
  defaultGraphColors,
  getTooltipFormatterCallback,
  createFlags,
  findGaps,
  getPlotLines
} from '@/services/graphing'
import { HTTPStatus } from '@/api'
import {
  measurementTypesKey,
  sensorTypesKey,
  measurementTypesLabelKey,
  MeasurementType
} from '@/services/device-measurements'
import { aqiTypeKeys, getEffectiveMeasurement, aqiMeasurementTypes } from '@/store/map/utils'
import { NAMESPACES } from '@/store/types'
import { MAP_GETTERS } from '@/store/map'
import { USER_GETTERS } from '@/store/user'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { DEVICE_UPDATE_INTERVAL_MS } from '@/services/device-metadata'
import { FeatureFlag } from '@/services/feature-flags'
import { isChildOverflowing } from '@/services/html-utilities'
import { UnitOfMeasure } from '@fusion/units'
import { convertUTCToTSITimezoneDate } from '@/helpers/common/helper'
import { getMeasurementSelectedUnit, type WidgetDevice } from '@/helpers/widgets/helper'
import { isValidObject, isValidDate } from '@/helpers/common/validationHelper'
import { useDisplay } from 'vuetify'
import type { TelemetryChartParams } from '@/types/devices'

const GRAPH_WIDTH = 'calc(100%+30px)'
const GRAPH_HEIGHT_SMALL = '278px'
const GRAPH_HEIGHT_MEDIUM = '400px'
const GRAPH_HEIGHT_FULLSCREEN = '600px'
const DIALOG_TITLE_HEIGHT = 36
const WIDGET_TITLE_HEIGHT = 42
const DEFAULT_ELEMENT_HEIGHT = 0
const TIME_PERIOD_DAY = 'day'
const TIME_PERIOD_WEEK = 'week'
const TIME_PERIOD_MONTH = 'month'
const TIME_PERIOD_THREE_MONTHS = 'threeMonths'
const TIME_PERIOD_CUSTOM = 'custom'
const ONE_DAY_MS = 1000 * 60 * 60 * 24
const ONE_WEEK_MS = ONE_DAY_MS * 7
const ONE_MONTH_MS = ONE_DAY_MS * 30
const ONE_DAY_ONE_SECOND_LOGGING = 60 * 60 * 24
const MAX_PAGES = 4

interface DateRange {
  start: string
  end: string
}

interface HistoricalDeviceData {
  deviceId: string
  telemetryData: [number, number | null][]
}

interface TelemetryPage {
  deviceId: string
  telemetryData: [number, number | null][]
}

export default defineComponent({
  name: 'HistoryGraph',
  setup() {
    const { xs } = useDisplay()
    const featureFlagsStore = FusionStores.featureFlags()
    const userProfileStore = FusionStores.userProfile(getAuthInstance())
    return { featureFlagsStore, userProfileStore, xs }
  },
  props: {
    allowDay: { type: Boolean, default: true },
    allowWeek: { type: Boolean, default: true },
    allowMonth: { type: Boolean, default: true },
    allowThreeMonths: { type: Boolean, default: true },
    allowCustom: { type: Boolean, default: true },
    isFullscreen: Boolean,
    isDetachedDialog: Boolean,
    removableDevices: Boolean,
    selectedDevices: { type: Array as PropType<WidgetDevice[]>, default: (): WidgetDevice[] => [] },
    selectedParameter: Number,
    selectedTimePeriod: String,
    accountId: String,
    width: Number,
    height: Number,
    permissions: Object
  },
  data() {
    return {
      isLoading: false,
      fullscreen: false,
      timePeriod: TIME_PERIOD_DAY,
      customDateRange: { start: null as Date | null, end: null as Date | null },
      graph: null as Highcharts.Chart | null,
      graphWidth: GRAPH_WIDTH,
      graphHeight: 0 as string | number,
      graphOptions: getDefaultGraphOptions(),
      graphColors: defaultGraphColors,
      loadDataInterval: null as ReturnType<typeof setInterval> | null,
      customResetZoomButton: null as HTMLElement | null,
      loadDataQueue: [] as Array<() => void | Promise<void>>,
      isResettingZoom: false,
      historicalData: [] as HistoricalDeviceData[],
      isFirstLoadComplete: false,
      redraw: false,
      totalCalls: 0,
      completedCalls: 0,
      completedPercentage: 0,
      showData: false
    }
  },
  created() {
    this.loadData = debounce(this.loadData, 250)
    this.clearGraph = debounce(this.clearGraph, 250)
    this.redrawGraph = debounce(this.redrawGraph, 250)
  },
  async mounted() {
    this.setGraphHeight()
    await this.initGraph()
  },
  updated() {
    this.setGraphHeight()
  },
  unmounted() {
    this.stopLoadDataInterval()
  },
  watch: {
    width() {
      this.setGraphHeight()
      this.reflowGraph()
    },
    height() {
      this.setGraphHeight()
      this.reflowGraph()
    },
    isFullscreen() {
      this.setGraphHeight()
      this.reflowGraph()
    },
    async selectedParameter() {
      await nextTick()
      this.refreshGraph()
    },
    async aqiType() {
      await nextTick()
      this.refreshGraph()
    },
    async selectedDevices() {
      await nextTick()
      //if the selected devices change we have to update time period if more restrictive
      if (
        this.onlyPublicDevices &&
        (this.timePeriod === TIME_PERIOD_WEEK ||
          this.timePeriod === TIME_PERIOD_MONTH ||
          this.timePeriod === TIME_PERIOD_THREE_MONTHS)
      ) {
        this.changeTimePeriod(TIME_PERIOD_DAY)
      } else {
        this.refreshGraph()
      }

      // Add the loadData function in a queue for new devices
      if (this.isFirstLoadComplete) {
        this.loadDataHandler()
      }
    },
    selectedTimePeriod(newValue: string) {
      this.timePeriod = newValue
    },
    async selectedUnitLabel(selectedUnitLabel: string, previousSelectedUnitLabel: string) {
      if (selectedUnitLabel !== previousSelectedUnitLabel) {
        await nextTick()
        this.redraw = true
        this.updateGraphTooltipFormatter()
        this.refreshGraph()
      }
    },
    showDataGapLine() {
      this.refreshGraph()
    },
    async loadDataForNewDevices(loadDataForNewDevices: boolean) {
      if (loadDataForNewDevices) {
        await this.loadDataQueue[0]()
        this.loadDataQueue = this.loadDataQueue.slice(1)
      }
    }
  },
  computed: {
    onlyPublicDevices: function () {
      for (const device of this.selectedDevices) {
        if (!this.isDevicePublic(device)) {
          return false
        }
      }
      return true
    },
    showCustomButton: function () {
      const customDatesFeatureFlagEnabled = this.featureFlagsStore.featureFlagBySlug(
        FeatureFlag.HistoryChartCustomDateRange
      )
      return this.allowCustom && customDatesFeatureFlagEnabled
    },
    timePeriodControl: function (): Record<string, boolean> {
      const periods: Record<string, boolean> = {
        day: false,
        week: false,
        month: false,
        threeMonths: false,
        custom: false
      }
      periods[this.timePeriod] = true
      return periods
    },
    measurementType: function () {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
    },
    aqiType: function () {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`]
    },
    effectiveParameter: function () {
      return getEffectiveMeasurement(this.selectedParameter, this.aqiType)
    },
    graphLabel: function () {
      return measurementTypesLabelKey[this.effectiveParameter]
        ? this.$t(`map.historyDialog.dropdown.${measurementTypesLabelKey[this.effectiveParameter]}`)
        : ''
    },
    showDataGapLine() {
      return this.$store.getters[`${NAMESPACES.USER}/${USER_GETTERS.GET_SHOW_DATA_GAP_LINE}`]
    },
    selectedUnitLabel() {
      return getMeasurementSelectedUnit(this.selectedParameter, this.$t.bind(this))
    },
    loadDataForNewDevices() {
      return !!this.loadDataQueue.length && !this.isLoading
    },
    showSpinnerWithPercentage() {
      return this.totalCalls > 1 && !this.showData
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    startLoadDataInterval() {
      if (this.loadDataInterval === null) {
        const refreshInterval = this.getRefreshInterval()
        this.loadDataInterval = setInterval(this.loadData, refreshInterval)
      }
    },
    stopLoadDataInterval() {
      if (this.loadDataInterval !== null) {
        clearInterval(this.loadDataInterval)
        this.loadDataInterval = null
      }
    },
    isDeviceIndoor(deviceId: string): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_INDOOR_BY_DEVICE_ID}`
      ](deviceId)
    },
    isDevicePublicById(deviceId: string): boolean {
      for (const device of this.selectedDevices) {
        if (deviceId === device.id) {
          return this.isDevicePublic(device)
        }
      }
      return true // if not found, treat as public
    },
    isDevicePublic(device: WidgetDevice | null | undefined): boolean {
      if (!device) {
        return true // treat null/undefined device as public
      }
      return !device.isMine && !device.isShared
    },
    isMeasurementSupported(device: WidgetDevice | null | undefined): boolean {
      if (device) {
        const supportedMeasurements = this.$store.getters[
          `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUPPORTED_MEASUREMENTS}`
        ](device.model, device.submodel, this.isDevicePublic(device))
        return supportedMeasurements.includes(this.selectedParameter)
      }
      return false
    },
    removeDevice(index: number, options?: { force?: boolean }) {
      this.$emit('removeDevice', index, options)
      if (this.selectedDevices.length === 0) {
        this.close()
      }
    },
    changeTimePeriod(period: string) {
      if (
        period === TIME_PERIOD_DAY ||
        (!this.onlyPublicDevices &&
          (period === TIME_PERIOD_WEEK ||
            period === TIME_PERIOD_MONTH ||
            period === TIME_PERIOD_THREE_MONTHS ||
            period === TIME_PERIOD_CUSTOM))
      ) {
        this.timePeriod = period
        this.clearGraph()
        this.$emit('graphPeriodChanged', period)
        this.loadData()
        if (period !== TIME_PERIOD_CUSTOM) {
          this.$emit('clearCustomDataRangeModel')
        }
      }
    },
    splitDateRange(
      startDate: string | null,
      endDate: string | null,
      numParts: number,
      isAConnectedDevice: boolean
    ): DateRange[] {
      const start = new Date(startDate as string)
      let end = new Date(endDate as string)
      const intervals: DateRange[] = []

      if (isValidDate(start) || isValidDate(end)) {
        if (start > end) {
          end = start
        }

        const totalMs = end.getTime() - start.getTime()
        // If the device is a DT, start the pagination process from one week on.
        // If the device is not a DT, start the pagination from one month on.
        const minimumMsForPagination = isAConnectedDevice ? ONE_WEEK_MS : ONE_MONTH_MS
        if (minimumMsForPagination > totalMs) {
          return [
            {
              start: new Date(start).toISOString(),
              end: new Date(end).toISOString()
            }
          ]
        }

        const basePartLength = Math.floor(totalMs / numParts)
        let currentStartMs = start.getTime()
        for (let i = 0; i < numParts; i++) {
          // If this is the last page, make the end = original endDate
          if (i === numParts - 1) {
            intervals.push({
              start: new Date(currentStartMs).toISOString(),
              end: new Date(end).toISOString()
            })
          } else {
            // Otherwise, end = currentStart + basePartLength
            const thisEndMs = currentStartMs + basePartLength
            intervals.push({
              start: new Date(currentStartMs).toISOString(),
              end: new Date(thisEndMs).toISOString()
            })
            // Next page start = this page end + 1 ms
            currentStartMs = thisEndMs + 1
          }
        }
      }

      return intervals
    },
    prepareHistoricalDataParams(id: string, measurementKey: MeasurementType): TelemetryChartParams {
      let start = new Date(this.getDeviceStartDate(id))
      const end = this.getEndDate()

      if (this.deviceHasHistoricalData(id)) {
        const telemetry = this.getHistoricalTelemetryData(id)
        const latestTimestamp = telemetry[telemetry.length - 1][0]
        start = new Date(latestTimestamp)
        start.setSeconds(start.getSeconds() + 1)
      }

      let tempUnit = 'c'
      if (this.userProfileStore.settings.tempUnit === UnitOfMeasure.DEGREES_F) {
        tempUnit = 'f'
      }

      const pressureUnit = this.userProfileStore.settings.pressureUnit || UnitOfMeasure.INCH_MERCURY
      const pmUnit =
        this.userProfileStore.settings.pmUnit || UnitOfMeasure.MICROGRAMS_PER_CUBIC_METER
      return {
        id: id,
        sensor: sensorTypesKey[measurementKey] as string,
        measurement: measurementTypesKey[measurementKey],
        tempUnit: tempUnit,
        pressureUnit,
        pmUnit,
        start: start ? start.toISOString() : null,
        end: end ? end.toISOString() : null
      }
    },
    // Return all the query params needed to return the device telemetry in pages
    getAllDevicesDataParams(): Record<string, TelemetryChartParams[]> {
      const allDevicesDataParams: Record<string, TelemetryChartParams[]> = {}
      this.selectedDevices.forEach((selectedDevice) => {
        const { id } = selectedDevice
        allDevicesDataParams[id] = []
        const supportedByDevice = this.isMeasurementSupported(selectedDevice)
        if (supportedByDevice) {
          const dataParams = this.prepareHistoricalDataParams(id, this.effectiveParameter)
          const isAConnectedDevice = this.isAConnectedDevice(id)
          const dateRanges = this.splitDateRange(
            dataParams.start,
            dataParams.end,
            MAX_PAGES,
            isAConnectedDevice
          )

          dateRanges.forEach((dateRange) => {
            const params: TelemetryChartParams = {
              ...dataParams,
              start: dateRange.start,
              end: dateRange.end
            }

            allDevicesDataParams[id].push(params)
          })
        }
      })

      return allDevicesDataParams
    },
    getCompletedPercentage(completedCalls: number, totalCalls: number): number {
      if (totalCalls === 0) return 0
      const completedPercentage = (completedCalls / totalCalls) * 100
      return Math.round(completedPercentage)
    },
    // Returns the number of endpoint calls needed for all devices to populate the chart
    getTotalCalls(allDevicesDataParams: Record<string, TelemetryChartParams[]>): number {
      if (isValidObject(allDevicesDataParams)) {
        return Object.values(allDevicesDataParams).reduce((sum, params) => {
          return sum + params.length
        }, 0)
      }

      return 0
    },
    getShowData() {
      return !!this.historicalData.reduce((sum, item) => sum + item.telemetryData.length, 0)
    },
    async getTelemetryPage(dataParams: TelemetryChartParams): Promise<TelemetryPage> {
      const page: TelemetryPage = {
        deviceId: dataParams.id,
        telemetryData: []
      }

      try {
        const resp = await this.$api.getTelemetryGraphData(
          dataParams as unknown as TelemetryChartParams
        )
        if (resp.status === HTTPStatus.OK) {
          const body = await resp.json()
          const readings = body.data || []
          page.telemetryData = readings as [number, number | null][]
        } else if (resp.status === HTTPStatus.NotFound) {
          const index = this.getIndexInSelectedDevices(dataParams.id)
          this.removeDevice(index, { force: true })
        }
      } catch {
        // Notify user "not able to retrieve data" with error toast in future story
      }

      return page
    },
    setHistoricalData(deviceId: string, deviceTelemetry: [number, number | null][]) {
      if (deviceTelemetry?.length) {
        if (this.deviceHasHistoricalData(deviceId)) {
          this.appendDataToHistoryChart(deviceId, deviceTelemetry)
        } else {
          this.historicalData.push({
            deviceId: deviceId,
            telemetryData: deviceTelemetry
          })
        }

        this.redraw = true
      }
    },
    async setDeviceTelemetryPages(
      deviceId: string,
      deviceDataParams: TelemetryChartParams[]
    ): Promise<void> {
      if (deviceDataParams?.length) {
        // Execute endpoint requests using pagination sequentially
        for (const params of deviceDataParams) {
          const telemetryPage = await this.getTelemetryPage(params)
          this.setHistoricalData(deviceId, telemetryPage.telemetryData)
          this.completedCalls++
          this.completedPercentage = this.getCompletedPercentage(
            this.completedCalls,
            this.totalCalls
          )
        }
      }
    },
    getChartEndpointRequests(
      allDevicesDataParams: Record<string, TelemetryChartParams[]>
    ): Promise<void>[] {
      if (isValidObject(allDevicesDataParams)) {
        return Object.keys(allDevicesDataParams).map(async (deviceId) => {
          // Only request the telemetry data for new added device to the chart
          if (this.loadDataForNewDevices && this.deviceHasHistoricalData(deviceId)) {
            return
          }

          const deviceDataParams = allDevicesDataParams[deviceId]
          await this.setDeviceTelemetryPages(deviceId, deviceDataParams)
        })
      }

      return []
    },
    removeOutdatedDataFromHistoryChart() {
      this.historicalData.forEach((deviceHistoricalData) => {
        const { deviceId } = deviceHistoricalData
        if (this.deviceHasHistoricalData(deviceId)) {
          const startDate = this.getDeviceStartDate(deviceId)
          const deviceIndex = this.getDeviceIndex(deviceId)

          if (deviceIndex !== -1) {
            const telemetryData = this.historicalData[deviceIndex].telemetryData
            let outdatedIndex = 0

            while (
              outdatedIndex < telemetryData.length &&
              telemetryData[outdatedIndex][0] < (startDate as number)
            ) {
              outdatedIndex++
            }

            // Remove all outdated telemetry at once
            if (outdatedIndex > 0) {
              this.redraw = true
              this.historicalData[deviceIndex].telemetryData.splice(0, outdatedIndex)
            }
          }
        }
      })
    },
    removeDeviceFromHistoricalData(deviceId: string) {
      const deviceIndex = this.getDeviceIndex(deviceId)
      if (deviceIndex !== -1) {
        this.historicalData.splice(deviceIndex, 1)
        const deviceSeriesIndex = this.getDeviceIndexInGraphSeries(
          this.selectedDevices[deviceIndex]?.friendlyName
        )
        if (deviceSeriesIndex !== -1) {
          this.graph!.series[deviceSeriesIndex]?.remove(false)
          this.redraw = true
        }
      }
    },
    removeDevicesWithoutTelemetryFromHistoryChart() {
      this.historicalData.forEach((deviceHistoricalData) => {
        const { deviceId } = deviceHistoricalData
        if (!this.deviceHasHistoricalData(deviceId)) {
          this.removeDeviceFromHistoricalData(deviceId)
        }
      })
    },
    async loadData() {
      // Do not call the chart endpoint if there's another endpoint request or a chart rendering action in progress
      if (this.graph && !this.isLoading) {
        this.isLoading = true
        // Show loading spinner with percentage completed
        this.completedCalls = 0
        this.completedPercentage = 0
        const allDevicesDataParams = this.getAllDevicesDataParams()
        this.totalCalls = this.getTotalCalls(allDevicesDataParams)
        const requests = this.getChartEndpointRequests(allDevicesDataParams)
        // Wait for all request to perform a single chart update
        await Promise.all(requests)
        this.removeOutdatedDataFromHistoryChart()
        this.removeDevicesWithoutTelemetryFromHistoryChart()
        this.showData = this.getShowData()

        // Re-render the history graph only if new telemetry was added/deleted
        if (this.redraw) {
          this.clearPlotLines()
          this.setGraphDataWithHistoricalData()
          this.graph.redraw()
          this.graph.reflow()
          // Preserve the zoom level when the chart adds/remove data
          if (!this.isResetZoomButtonVisible()) {
            // Adjust the min/max in the navigator
            this.customResetZoomButtonClickHandler()
          }
          this.redraw = false
        }
        this.isLoading = false
        this.startLoadDataInterval()

        if (!this.isFirstLoadComplete) {
          this.isFirstLoadComplete = true
        }
      }
    },
    deviceHasHistoricalData(deviceId: string): boolean {
      return this.historicalData.some((data) => data.deviceId === deviceId)
    },
    getHistoricalTelemetryData(deviceId: string): [number, number | null][] {
      return this.historicalData
        .filter((telemetry) => telemetry.deviceId === deviceId)
        .map((telemetry) => telemetry.telemetryData)[0]
    },
    appendDataToHistoryChart(deviceId: string, reading: [number, number | null][] = []) {
      const deviceIndex = this.getDeviceIndex(deviceId)
      if (deviceIndex !== -1) {
        this.historicalData[deviceIndex].telemetryData =
          this.historicalData[deviceIndex].telemetryData.concat(reading)
      }
    },
    getDeviceIndex(deviceId: string): number {
      return this.historicalData.findIndex((data) => data.deviceId === deviceId)
    },
    getEndDate() {
      if (this.timePeriodControl.custom) return this.customDateRange.end
      return new Date()
    },
    getDeviceStartDate(deviceId: string): number | Date | null {
      const minDays = 1
      const maxDays = 94
      const isPublicDevice = this.isDevicePublicById(deviceId)
      let age = isPublicDevice ? minDays : maxDays // defaults
      if (this.timePeriodControl.custom)
        return this.getDeviceAllowedStartDate(age * ONE_DAY_MS, this.customDateRange)

      const start = new Date()
      if (this.timePeriodControl.day) age = minDays
      if (this.timePeriodControl.week && !isPublicDevice) age = 7
      if (this.timePeriodControl.month && !isPublicDevice) age = 30
      if (this.timePeriodControl.threeMonths && !isPublicDevice) age = maxDays

      return start.setDate(start.getDate() - age)
    },
    getDeviceAllowedStartDate(
      maxAllowedDeviceMS: number,
      customDateRange: { start: Date | null; end: Date | null } | null
    ): Date | null {
      if (customDateRange) {
        const { start, end } = customDateRange

        if (start && end) {
          const maxStartDate = new Date(
            new Date(convertUTCToTSITimezoneDate(end.toISOString())).getTime() - maxAllowedDeviceMS
          )
          if (start.getTime() < maxStartDate.getTime()) {
            return maxStartDate
          }
        }

        return start
      }

      return null
    },
    getSelectedDeviceIndex(id: string): number {
      const index = this.selectedDevices.findIndex((selectedDevice) => {
        return selectedDevice.id === id
      })
      return index === -1 ? 0 : index
    },
    preprocess(arr: [number, number | null][]): [[number, number | null][], number[]] {
      const [tmpArr, gaps, intervalChanges] = findGaps(arr)
      const gapPoints = gaps.map((ts) => [ts, null] as [number, null])
      arr = (tmpArr as [number, number | null][]).concat(gapPoints)
      arr.sort((ptA, ptB) => {
        return ptA[0] - ptB[0]
      })
      return [arr, intervalChanges]
    },
    setGraphFlags(readings: [number, number | null][], seriesColor: string) {
      if (this.graph) {
        let flags: ReturnType<typeof createFlags> | Record<string, never> = {}
        const measurements = aqiMeasurementTypes
        if (readings || measurements.includes(this.effectiveParameter)) {
          const aqiKey = (aqiTypeKeys as Record<number, string>)[this.aqiType] || 'us'
          const aqiLabel = this.$t(`map.mapSettings.aqiTypeLabels.${aqiKey}`)
          const invalidAqiText = this.$t('map.historyDialog.graph.invalidAqiMessage.text', {
            aqiType: aqiLabel
          })
          flags = createFlags(readings, seriesColor, invalidAqiText)
        }

        const flagIndex = this.getDeviceIndexInGraphSeries(`AQI flag ${seriesColor}`)
        if (flagIndex === -1) {
          this.graph.addSeries(flags as unknown as Highcharts.SeriesOptionsType, false, false)
        } else {
          this.graph.series[flagIndex].setData(
            flags as unknown as Highcharts.PointOptionsType[],
            false,
            false
          )
        }
      }
    },
    setGraphDataGaps(telemetryWithGaps: [number, number | null][], seriesColor: string) {
      if (this.graph) {
        const dataGaps = this.showDataGapLine
          ? getPlotLines(telemetryWithGaps, seriesColor, this.graph as unknown as Highcharts.Chart)
          : []
        this.updatePlotLines(
          (this.graphOptions.xAxis as Highcharts.XAxisOptions).plotLines?.concat(dataGaps)
        )
      }
    },
    setGraphDataWithHistoricalData() {
      let publicIndex = 1

      this.historicalData.forEach((deviceHistoricalData) => {
        const { deviceId, telemetryData } = deviceHistoricalData
        const readings = telemetryData || []
        const index = this.getSelectedDeviceIndex(deviceId)
        const seriesColor = this.graphColors[index]
        const [telemetryWithGaps] = this.preprocess(readings)

        if (this.graph) {
          let deviceName = this.selectedDevices[index].friendlyName.trim()
          if (!deviceName) {
            deviceName = `${this.$t('map.popover.publicDevice')} ${publicIndex}`
            publicIndex++
          }
          const deviceSeriesIndex = this.getDeviceIndexInGraphSeries(deviceName)
          if (deviceSeriesIndex === -1) {
            this.graph.addSeries(
              {
                type: 'line',
                data: telemetryWithGaps as Highcharts.PointOptionsType[],
                connectNulls: false,
                lineWidth: 0.5,
                name: deviceName,
                color: seriesColor
              },
              true,
              false
            )
          } else {
            this.graph.series[deviceSeriesIndex].setData(
              telemetryWithGaps as Highcharts.PointOptionsType[],
              false,
              false
            )
          }

          this.setGraphFlags(readings, seriesColor)
          this.setGraphDataGaps(telemetryWithGaps, seriesColor)
        }
      })
    },
    async initGraph() {
      if (Highcharts) {
        const ref = this.$refs['graph']
        if (ref) {
          this.setGraphEvents()
          this.graph = markRaw(
            Highcharts.chart(ref as HTMLElement, this.graphOptions as Highcharts.Options)
          )
          this.updateGraphTooltipFormatter()
          await this.loadData()
          this.setCustomResetZoomButton()
        }
      }
    },
    async reflowGraph() {
      if (this.graph) {
        await nextTick()
        this.graph.reflow()
      }
    },
    redrawGraph() {
      if (this.redraw) {
        this.graph?.redraw()
        this.redraw = false
      }
    },
    setGraphEvents() {
      this.graphOptions.chart!.events = {
        selection: this.selectionHandler as unknown as Highcharts.ChartSelectionCallbackFunction
      }
      ;(this.graphOptions.xAxis as Highcharts.XAxisOptions).events = {
        setExtremes: this.setExtremesHandler
      }
      this.graphOptions.plotOptions!.series!.events = {
        mouseOver: (event) => {
          this.focusPlotLinesByColor((event?.target as { color?: string })?.color || '')
        },
        mouseOut: () => {
          this.resetPlotLinesStyle()
        }
      }
    },
    setCustomDateRange(customDateRange: { start: Date | null; end: Date | null }) {
      if (!customDateRange) return
      this.customDateRange = customDateRange
      this.changeTimePeriod(TIME_PERIOD_CUSTOM)
    },
    selectionHandler() {
      this.showCustomResetZoomButton()
    },
    setExtremesHandler(event: Highcharts.AxisSetExtremesEventObject) {
      if (this.isResettingZoom) return
      if (this.graph && this.customResetZoomButton) {
        const { dataMin, dataMax, min, max } = event
        // The user has zoomed out by using the navigator
        if (
          min === dataMin &&
          max === dataMax &&
          this.customResetZoomButton.style.display === 'block'
        ) {
          this.graph.zoomOut()
          this.hideCustomResetZoomButton()
          // The user has zoomed in by using the navigator
        } else if (this.customResetZoomButton.style.display === 'none') {
          this.showCustomResetZoomButton()
        }
      }
    },
    indoorIconSrc(model: string): string {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_PIN_PATH}`
      ](model, 'smallIndoor')
    },
    outdoorIconSrc(model: string): string {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_PIN_PATH}`
      ](model, 'smallOutdoor')
    },
    getDeviceTooltip(device: WidgetDevice): string {
      const message = device.friendlyName
      if (!this.isMeasurementSupported(device)) {
        return this.$t('map.historyDialog.notSupported', {
          measurement: this.graphLabel
        })
      } else if (!this.deviceHasHistoricalData(device?.id)) {
        return this.$t('map.historyDialog.timePeriodNotAllowed')
      }
      return message
    },
    getButtonColor(period: string): string {
      return this.timePeriodControl[period] ? 'primary' : ''
    },
    isDeviceActive(device: WidgetDevice): boolean {
      return this.isMeasurementSupported(device) && this.deviceHasHistoricalData(device?.id)
    },
    disableDeviceTooltip(device: WidgetDevice): boolean {
      return (
        !isChildOverflowing(this.$refs[device.id] as HTMLElement) && this.isDeviceActive(device)
      )
    },
    getCardElsHeight() {
      // combined height of non-graph elements
      const title = this.isDetachedDialog ? DIALOG_TITLE_HEIGHT : WIDGET_TITLE_HEIGHT
      // In Vue 3, refs to Vuetify components return the component instance; use $el to get the DOM element
      const getRefHeight = (ref: unknown): number => {
        if (!ref) return DEFAULT_ELEMENT_HEIGHT
        const el = (ref as { $el?: HTMLElement }).$el ?? (ref as HTMLElement)
        return el?.clientHeight ?? DEFAULT_ELEMENT_HEIGHT
      }
      const tpc = getRefHeight(this.$refs.timePeriodControl)
      const hgl = getRefHeight(this.$refs.historyGraphLabel)
      const sd = getRefHeight(this.$refs.selectedDevices)
      const mmm = getRefHeight(this.$refs.maximumMetMessage)
      return title + tpc + hgl + sd + mmm
    },
    setGraphHeight() {
      let graphHeight: string = GRAPH_HEIGHT_MEDIUM
      const cardElsHeight = this.getCardElsHeight()

      if (this.isFullscreen) {
        graphHeight = GRAPH_HEIGHT_FULLSCREEN
      } else if (this.isDetachedDialog) {
        graphHeight = GRAPH_HEIGHT_SMALL
      } else if (this.height) {
        graphHeight = `${this.height - cardElsHeight}px`
      }

      this.graphHeight = graphHeight
    },
    getIndexInSelectedDevices(deviceId: string): number {
      return this.selectedDevices.findIndex((device) => device.id === deviceId)
    },
    loadDataHandler() {
      this.loadDataQueue.push(this.loadData)
    },
    clearGraph() {
      if (this.isFirstLoadComplete && this.graph) {
        this.stopLoadDataInterval()
        this.historicalData = []
        this.showData = false
        while (this.graph.series.length) {
          this.graph.series[0].remove(false)
        }
        this.customResetZoomButtonClickHandler()
        this.clearPlotLines()
        this.graph.redraw()
      }
    },
    updatePlotLines(plotLines: Highcharts.XAxisPlotLinesOptions[] | undefined, redraw = false) {
      if (this.graph && typeof this.graph.update === 'function' && plotLines) {
        ;(this.graphOptions.xAxis as Highcharts.XAxisOptions).plotLines = plotLines
        this.graph.update(
          {
            xAxis: {
              plotLines: plotLines
            }
          },
          redraw
        )
      }
    },
    clearPlotLines() {
      this.updatePlotLines([])
      // Clear the graph label if it's visible on the chart
      type GraphWithCustomTooltip = Highcharts.Chart & {
        customTooltip?: { destroy(): void } | null
      }
      ;(this.graph as GraphWithCustomTooltip).customTooltip?.destroy()
      ;(this.graph as GraphWithCustomTooltip).customTooltip = null
    },
    isResetZoomButtonVisible() {
      return this.customResetZoomButton?.style?.display === 'block'
    },
    async showCustomResetZoomButton() {
      await nextTick()
      if (!this.isResetZoomButtonVisible()) {
        this.customResetZoomButton!.style.display = 'block'
      }
    },
    async hideCustomResetZoomButton() {
      await nextTick()
      if (this.isResetZoomButtonVisible()) {
        this.customResetZoomButton!.style.display = 'none'
      }
    },
    customResetZoomButtonClickHandler() {
      this.isResettingZoom = true
      this.graph?.zoomOut()
      this.hideCustomResetZoomButton()
      this.isResettingZoom = false
    },
    setCustomResetZoomButton() {
      if (!this.customResetZoomButton) {
        this.customResetZoomButton =
          (this.$refs['customResetZoomButton'] as { $el?: HTMLElement } | undefined)?.$el ?? null
        if (this.customResetZoomButton) {
          this.customResetZoomButton.style.display = 'none'
        }
      }
    },
    refreshGraph() {
      this.clearGraph()
      this.loadData()
    },
    focusPlotLinesByColor(color: string) {
      const xAxis = this.graphOptions?.xAxis as Highcharts.XAxisOptions
      if (xAxis?.plotLines) {
        const newPlotLines = xAxis.plotLines.map((plotLine) => {
          return plotLine.color !== color && (plotLine.dashStyle as string) === 'dash'
            ? {
                ...plotLine,
                width: 0.2
              }
            : plotLine
        })
        this.updatePlotLines(newPlotLines, true)
      }
    },
    resetPlotLinesStyle() {
      const xAxis = this.graphOptions?.xAxis as Highcharts.XAxisOptions
      if (xAxis?.plotLines) {
        const newPlotLines = xAxis.plotLines.map((plotLine) => {
          return (plotLine.dashStyle as string) === 'dash'
            ? {
                ...plotLine,
                width: 1
              }
            : plotLine
        })
        this.updatePlotLines(newPlotLines, true)
      }
    },
    getDeviceIndexInGraphSeries(friendlyName: string): number {
      if (this.graph) {
        return this.graph.series.findIndex((series) => {
          return series.name === friendlyName
        })
      }

      return -1
    },
    isAConnectedDevice(deviceId: string): boolean {
      return !!this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID}`
      ](deviceId)
    },
    updateGraphTooltipFormatter() {
      this.graph?.update(
        {
          tooltip: {
            formatter: getTooltipFormatterCallback(this.selectedUnitLabel)
          }
        },
        false
      )
    },
    getRefreshInterval() {
      // If at least one device is a DT and has more than 86,400 data points
      const changeRefreshInterval = this.historicalData.some(
        (data) =>
          this.isAConnectedDevice(data.deviceId) &&
          data.telemetryData?.length > ONE_DAY_ONE_SECOND_LOGGING
      )
      return changeRefreshInterval ? 5 * DEVICE_UPDATE_INTERVAL_MS : DEVICE_UPDATE_INTERVAL_MS
    }
  }
})
</script>

<style lang="scss">
@media (min-width: 400px) {
  .history-dialog {
    position: absolute;
    right: 10px;
    top: 70px;
  }
}

.history-chart-wrapper {
  .highcharts-reset-zoom {
    display: none;
  }

  .v-progress-circular {
    height: 50px !important;
    width: 50px !important;
  }

  .empty-history-chart {
    .highcharts-axis-line,
    .highcharts-navigator {
      display: none;
    }
  }
}

.widget-fullscreen,
.v-dialog--fullscreen {
  .history-chart-wrapper {
    .loading-container {
      font-size: 24px;
    }

    .v-progress-circular {
      height: 75px !important;
      width: 75px !important;
    }
  }
}
</style>

<style lang="scss" scoped>
@use '@/assets/global-mixins' as *;

.restrictedContent {
  cursor: not-allowed;
  opacity: 0.36;
}
.v-card {
  padding: 0 !important;
}

.v-card-actions {
  min-height: 0px;
  .timePeriodControl {
    background-color: #ececec;
    padding: 6px 16px 6px 16px;
    width: calc(100% + 12px);
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    margin-left: -6px;
    margin-right: -6px;
    &__label {
      font-size: 13px;
      line-height: 21px;
    }
    &__controls {
      white-space: nowrap;
      margin-left: 4px;
    }
    .v-btn {
      margin-left: 8px;
      padding: 0 10px;
      height: 24px;
      font-size: 11px;
      transition-property: all;
    }
  }
}
.card-content {
  padding: 0 0.4rem 0.4rem 0.4rem;
  .v-card-subtitle {
    padding: 8px 8px 8px 16px;
    opacity: 1;
  }
  .selected-devices {
    margin: 0;
    padding: 4px;
    div {
      max-width: calc(100% - 8px);
      margin: 8px 4px 8px 8px;
      font-size: 12px;
      transition: opacity 0.2s;
      &.inactive {
        opacity: 0.5;
      }
      .selected-device-icon {
        margin-right: 6px;
        height: 14px;
      }
      .device-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .v-icon {
        cursor: pointer;
        font-size: 18px;
      }
    }
  }
  .maximum-met-message {
    font-size: 12px;
    color: grey;
    padding: 0 8px 8px;
  }
}

.text-dark {
  color: black;
}

.opacity-20 {
  opacity: 0.2;
}

.loading-container {
  @include center-absolute-element;
  top: 45%;
  opacity: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  font-size: 18px;
}

.empty-message {
  @include center-absolute-element;
  width: 98%;
  text-align: center;
  opacity: 1;
  color: rgba(0, 0, 0, 0.6);
}

.measurement-label-wrapper {
  position: relative;
}

.reset-zoom-button {
  display: none;
  position: absolute;
  right: 8px;
  top: 8px;
  font-size: 11px;
  font-weight: bolder;
  color: black;
}

.history-chart-wrapper {
  position: relative;
  :deep(.highcharts-container) {
    height: 100% !important;
  }
}
</style>
